import { Link } from "react-router-dom";
import { BrandMark } from "@/components/BrandMark";

const UPDATED = "June 15, 2026";
const CONTACT = "support@5ads.ai";

const privacySections = [
  {
    h: "1. Who we are",
    p: [
      "5ads.ai (\"we\", \"us\", \"our\") is an AI-powered advertising management platform available at social.5ads.ai. It helps businesses connect their Meta (Facebook/Instagram) ad accounts, generate ad creatives, and optimize campaigns with the help of AI assistants.",
      "This Privacy Policy explains what information we collect, how we use it, and the choices you have. By using 5ads.ai you agree to this policy.",
    ],
  },
  {
    h: "2. Information we collect",
    list: [
      "Account information: your name, email address and (for password sign-ins) a securely hashed password. If you sign in with Google, we receive your name, email and profile picture from Google.",
      "Meta advertising data: when you connect your Facebook account via Meta's official OAuth flow, we receive an access token and, with your permission, read your ad accounts, campaigns, ad sets, ads, audiences, performance insights and lead form submissions through the Meta Marketing API.",
      "Brand assets: logos, brand colors and creative preferences you upload to your workspace Brand Kit.",
      "Billing information: payments are processed by Razorpay. We store only order references, plan and payment status — never your card or banking details.",
      "Usage data: basic technical logs (such as API activity and feature usage) used to operate quotas, improve reliability and prevent abuse.",
    ],
  },
  {
    h: "3. How we use your information",
    list: [
      "To operate your workspace: syncing your Meta campaigns, showing performance insights and managing leads.",
      "To power AI features: campaign data and brand assets are sent to our AI providers (Anthropic Claude and Google Gemini) solely to generate recommendations, ad copy and creatives for your workspace. They are not used to train third-party models.",
      "To process payments and enforce plan quotas.",
      "To communicate with you about your account, and to provide support.",
      "We never sell your personal data or your advertising data to anyone.",
    ],
  },
  {
    h: "4. Meta platform data",
    p: [
      "Our use of data received from Meta APIs adheres to the Meta Platform Terms and Developer Policies, including the Limited Use requirements. Meta data is stored isolated per workspace, is used only to provide the features you request, and is never shared with other workspaces or third parties.",
      "You can revoke our access at any time from your Facebook settings (Settings → Business integrations) or by disconnecting inside the app.",
    ],
  },
  {
    h: "5. Data deletion",
    p: [
      "You are in control of your data. You can delete it in any of the following ways:",
    ],
    list: [
      "In-app disconnect: go to the Connect page inside 5ads.ai and click \"Disconnect\". This immediately deletes your Meta access token and wipes all synced advertising data (accounts, campaigns, ads, audiences, insights and leads) from our servers.",
      "Facebook removal: remove 5ads.ai from Facebook → Settings & privacy → Settings → Business integrations. Then use the in-app disconnect (or contact us) to erase already-synced data.",
      `Full account deletion: email us at ${CONTACT} from your registered email with the subject "Delete my account". We will permanently delete your account, workspace and all associated data within 30 days and confirm by email.`,
    ],
  },
  {
    h: "6. Data retention & security",
    list: [
      "Data is retained only while your account is active or as needed to comply with legal obligations.",
      "Access tokens are stored encrypted at rest; all traffic is encrypted in transit (HTTPS/TLS).",
      "Each customer workspace is strictly isolated — no data is ever shared across workspaces.",
    ],
  },
  {
    h: "7. Third-party services",
    p: [
      "We rely on the following processors to provide the service: Meta Platforms (advertising APIs), Google (sign-in and Gemini AI), Anthropic (Claude AI), and Razorpay (payments). Each processes data only as needed to deliver their function.",
    ],
  },
  {
    h: "8. Your rights",
    p: [
      `Depending on your jurisdiction, you may have the right to access, correct, export or erase your personal data, and to object to or restrict certain processing. To exercise any of these rights, email ${CONTACT}. We respond within 30 days.`,
    ],
  },
  {
    h: "9. Changes & contact",
    p: [
      "We may update this policy from time to time; material changes will be announced in-app. Continued use after changes means you accept the updated policy.",
      `Questions? Contact us at ${CONTACT}.`,
    ],
  },
];

const termsSections = [
  {
    h: "1. Agreement",
    p: [
      "These Terms of Service (\"Terms\") govern your use of 5ads.ai, available at social.5ads.ai (the \"Service\"). By creating an account or using the Service you agree to these Terms. If you use the Service on behalf of a business, you represent that you are authorized to bind that business.",
    ],
  },
  {
    h: "2. The service",
    p: [
      "5ads.ai lets you connect Meta (Facebook/Instagram) ad accounts, view and manage campaigns, generate AI-assisted ad creatives and copy, receive AI optimization recommendations, and manage leads — all subject to your explicit approval before any change is applied to your ad accounts.",
      "AI-generated content and recommendations are suggestions only. You review and approve everything before it reaches your ad account, and you remain solely responsible for the ads you publish.",
    ],
  },
  {
    h: "3. Your account & responsibilities",
    list: [
      "You must provide accurate information and keep your credentials secure. You are responsible for all activity under your account.",
      "You must have the legal right to manage any ad account you connect.",
      "You agree to comply with Meta's Advertising Standards and all applicable laws when running ads through the Service.",
      "You may not misuse the Service: no unlawful content, no attempts to breach workspace isolation, no reverse engineering, scraping or reselling without permission.",
    ],
  },
  {
    h: "4. Plans, billing & quotas",
    list: [
      "Paid plans are billed through Razorpay. Prices, quotas (such as AI generations and API syncs) and plan durations are shown on the Billing page at the time of purchase.",
      "Plans run for the stated period (e.g., 30 days) from activation. Quotas reset per plan cycle and unused quota does not roll over.",
      "Fees are non-refundable except where required by law. Taxes may apply based on your location.",
      "We may change pricing prospectively; changes never affect a period you have already paid for.",
    ],
  },
  {
    h: "5. Third-party platforms",
    p: [
      "The Service depends on third-party platforms — in particular the Meta Marketing API, Google sign-in, and AI providers (Anthropic, Google). We are not responsible for outages, policy changes or restrictions imposed by these platforms. 5ads.ai is an independent tool and is not affiliated with, sponsored by, or endorsed by Meta Platforms, Inc. or Google LLC.",
    ],
  },
  {
    h: "6. Intellectual property",
    list: [
      "You retain all rights to your data, brand assets and the ad creatives generated for your workspace.",
      "We retain all rights to the Service, its software, design and branding.",
      "You grant us a limited license to process your content solely to operate the Service for you.",
    ],
  },
  {
    h: "7. Disclaimers & limitation of liability",
    p: [
      "The Service is provided \"as is\" without warranties of any kind. We do not guarantee specific advertising results, ad approval by Meta, or uninterrupted availability.",
      "To the maximum extent permitted by law, our total liability arising out of or relating to the Service is limited to the amount you paid us in the 3 months preceding the claim. We are not liable for indirect, incidental or consequential damages, including lost profits or ad spend.",
    ],
  },
  {
    h: "8. Termination",
    p: [
      "You may stop using the Service and delete your account at any time (see our Privacy Policy for data deletion). We may suspend or terminate accounts that violate these Terms or create risk for the platform. Upon termination, your data is deleted in accordance with our Privacy Policy.",
    ],
  },
  {
    h: "9. General",
    p: [
      "These Terms are governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts of Noida, Uttar Pradesh, India.",
      `We may update these Terms from time to time; material changes will be announced in-app. Questions? Contact us at ${CONTACT}.`,
    ],
  },
];

const PAGES = {
  privacy: { title: "Privacy Policy", sections: privacySections, testid: "privacy-page" },
  terms: { title: "Terms of Service", sections: termsSections, testid: "terms-page" },
};

export default function Legal({ kind = "privacy" }) {
  const page = PAGES[kind];
  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#2D2422] font-figtree" data-testid={page.testid}>
      <header className="fixed top-0 inset-x-0 z-50 border-b border-[#F0EBE1] bg-[#FAF9F6]/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 h-16">
          <Link to="/" data-testid="legal-logo"><BrandMark size={28} /></Link>
          <div className="flex items-center gap-5 text-sm font-semibold text-[#8a7f76]">
            <Link to="/privacy" className={`hover:text-[#2D2422] transition-colors ${kind === "privacy" ? "text-[#FF6B35]" : ""}`} data-testid="legal-nav-privacy">Privacy</Link>
            <Link to="/terms" className={`hover:text-[#2D2422] transition-colors ${kind === "terms" ? "text-[#FF6B35]" : ""}`} data-testid="legal-nav-terms">Terms</Link>
            <Link to="/login" className="hover:text-[#2D2422] transition-colors" data-testid="legal-nav-login">Log in</Link>
            <Link to="/signup" data-testid="legal-nav-signup"
              className="rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white text-xs font-bold px-4 py-2 transition-transform hover:-translate-y-0.5 active:scale-95 shadow-[0_8px_30px_rgba(255,107,53,0.25)]">
              Sign up free
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pt-32 pb-16">
        <h1 className="font-outfit text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="legal-title">{page.title}</h1>
        <p className="mt-3 text-sm text-[#8a7f76]">Last updated: {UPDATED} · Applies to social.5ads.ai</p>

        <div className="mt-12 space-y-10">
          {page.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-outfit text-lg font-bold text-[#2D2422]">{s.h}</h2>
              {s.p?.map((t, i) => (
                <p key={i} className="mt-3 text-sm leading-relaxed text-[#6f645b]">{t}</p>
              ))}
              {s.list && (
                <ul className="mt-3 space-y-2">
                  {s.list.map((t, i) => (
                    <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-[#6f645b]">
                      <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-[#FF6B35] shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </main>

      <footer className="bg-[#2D2422] text-[#a4988c]" data-testid="legal-footer">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="flex flex-wrap items-center justify-between gap-4 text-xs">
            <BrandMark size={24} dark />
            <span className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <span>© {new Date().getFullYear()} 5ads.ai. Built for Meta advertisers.</span>
            </span>
          </div>
          <p className="mt-6 pt-5 border-t border-white/10 text-[11px] leading-relaxed text-[#FAF9F6]/50">
            Disclaimer: 5ads.ai is an independent software tool and is not affiliated with, sponsored by, or endorsed by
            Meta Platforms, Inc. or Google LLC. Facebook, Instagram and Meta are trademarks of Meta Platforms, Inc.
            Gemini is a trademark of Google LLC. All product names, logos and brands are property of their respective owners.
          </p>
        </div>
      </footer>
    </div>
  );
}
